import React, { useState, useEffect, useMemo, useCallback } from "react";
import { getPasses } from '../../api/pass';
import { createPassPurchase } from '../../api/passPurchase';
import { Button, Card, Typography, Divider, message, Modal, List, Tabs } from "antd";
import { ShoppingCartOutlined, CloseOutlined, CreditCardOutlined, HomeOutlined } from "@ant-design/icons";
import PassTable from '../../components/Passes/passTable';
import PassForm from '../../components/Passes/passForm';
import { useSettings } from "../../contexts/settingsContext";
import { generatePurchasedPassesPDF } from "../../utils/passPurchaseUtils";
import { useNavigate } from 'react-router-dom';

const { Title } = Typography;
const { TabPane } = Tabs;

const BuyPasses = () => {
    const [loading, setLoading] = useState(true);
    const [submitting, setSubmitting] = useState(false);
    const [passes, setPasses] = useState([]);
    const [selectedPass, setSelectedPass] = useState(null);
    const [error, setError] = useState(null);
    const [isPopupVisible, setIsPopupVisible] = useState(false);
    const [isCreating, setIsCreating] = useState(false);
    const { state: settings } = useSettings();
    const [userPassPurchases, setUserPassPurchases] = useState([]);
    const [filterName, setFilterName] = useState('');
    const [checkoutVisible, setCheckoutVisible] = useState(false);
    const [name, setName] = useState('');
    const [idNumber, setIdNumber] = useState('');
    const navigate = useNavigate();

    // Pagination state for user pass purchases    
    const [userPassPurchasesPage, setUserPassPurchasesPage] = useState(1);
    const [userPassPurchasesPageSize, setUserPassPurchasesPageSize] = useState(10);

    useEffect(() => {
        const fetchPasses = async () => {
            try {
                const passesData = await getPasses({ credentials: 'include' });
                setPasses(passesData);
            } catch (err) {
                console.error("Error fetching passes", err);
                setError("Failed to load passes. Please try again later.");
            } finally {
                setLoading(false);
            }
        };
        fetchPasses();
    }, []);

    const openPopup = (pass) => {
        setSelectedPass(pass);
        setIsPopupVisible(true);
        setIsCreating(!pass);
    };

    const closePopup = () => {
        setIsPopupVisible(false);
    };

    const handleNewPassSubmit = (values) => {
        setSubmitting(true);
        try {
            setUserPassPurchases(prevPasses => [...prevPasses, { ...selectedPass, ...values }]);
            message.success('The pass has been successfully added!');
        } catch (err) {
            console.error("Error creating pass", err);
            message.error(`Failed to add pass. ${err.message}`);
        } finally {
            setSubmitting(false);
            closePopup();
        }
    };

    const openCheckoutModal = () => {
        setCheckoutVisible(true);
    };

    const closeCheckoutModal = () => {
        setCheckoutVisible(false);
    };

    const removePass = useCallback((pass) => {
        setUserPassPurchases(prevPasses => prevPasses.filter(item => item !== pass));
    }, []);

    const handlePurchase = async () => {
        const passPurchaseData = userPassPurchases.map(pass => ({
            pass: pass._id,
            id_number: pass.idNumber,
            price: pass.price,
            name: pass.name,
            competition: settings.selectedCompetition
        }));

        try {
            await Promise.all(passPurchaseData.map(data => createPassPurchase(data, { credentials: 'include' })));
            generatePurchasedPassesPDF(userPassPurchases, name, idNumber);
            setUserPassPurchases([]);
            setName('');
            setIdNumber('');
            closeCheckoutModal();
            message.success('Your passes have been successfully purchased!');
        } catch (error) {
            message.error('Failed to complete purchase. Please try again.');
        }
    };

    // Move all hooks to the top level, before any returns  
    const filteredUserPassPurchases = useMemo(() => {
        return userPassPurchases.filter(item => item.name && item.name.toLowerCase().includes(filterName.toLowerCase()));
    }, [userPassPurchases, filterName]);

    const userPassPurchasesToShow = useMemo(() => {
        const startIndex = (userPassPurchasesPage - 1) * userPassPurchasesPageSize;
        const endIndex = userPassPurchasesPage * userPassPurchasesPageSize;
        return filteredUserPassPurchases.slice(startIndex, endIndex);
    }, [filteredUserPassPurchases, userPassPurchasesPage, userPassPurchasesPageSize]);

    const totalPrice = useMemo(() => {
        return userPassPurchases.reduce((sum, pass) => sum + (pass.price || 0), 0);
    }, [userPassPurchases]);

    // After calling all hooks, we can perform any conditional returns  
    if (error) {
        return <div className="error-message">{error}</div>;
    }

    return (
        <div className="content-container" style={{ padding: '20px' }}>
            <div className="content-box" style={{ margin: '0 auto', maxWidth: '1200px' }}>
                <div style={{ display: 'flex', marginBottom: '20px', height: '50px', alignItems: 'center', justifyContent: 'space-between' }}>
                    <Title level={4} style={{ margin: 0 }}>Buy Passes</Title>
                    <div>
                        <Button type="default" icon={<HomeOutlined />} onClick={() => navigate('/')} className="back-to-login-button" style={{ marginRight: 8 }}>
                            Back to Login
                        </Button>
                        <Button type="primary" icon={<ShoppingCartOutlined />} onClick={openCheckoutModal} style={{ padding: '0 20px' }}>
                            Checkout ({userPassPurchases.length})
                        </Button>
                    </div>
                </div>
                <Card bordered={false}>
                    <Tabs defaultActiveKey="1">
                        <TabPane tab="Add Passes" key="1">
                            <PassTable
                                passes={passes}
                                loading={loading}
                                openPopup={openPopup}
                            />
                        </TabPane>
                    </Tabs>
                </Card>
                <PassForm
                    visible={isPopupVisible && !isCreating}
                    onCancel={closePopup}
                    onSubmit={handleNewPassSubmit}
                    selectedPass={selectedPass}
                    submitting={submitting}
                />
                <Modal
                    visible={checkoutVisible}
                    onCancel={closeCheckoutModal}
                    footer={null}
                >
                    <Title level={4}>Shopping Cart</Title>
                    <Divider />
                    <List
                        dataSource={userPassPurchasesToShow}
                        renderItem={item => (
                            <List.Item
                                actions={[
                                    <Button
                                        type="text"
                                        icon={<CloseOutlined />}
                                        onClick={() => removePass(item)}
                                    />
                                ]}
                            >
                                <List.Item.Meta
                                    title={item.day}
                                    description={`${item.type} - R${item.price}`}
                                />
                            </List.Item>
                        )}
                    />
                    <Divider />
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', paddingRight: '16px' }}>
                        <Button type="primary" icon={<CreditCardOutlined />} onClick={handlePurchase}>
                            Purchase
                        </Button>
                        <Title level={5} style={{ margin: 0 }}>Total Price: R{totalPrice.toFixed(2)}</Title>
                    </div>
                </Modal>
            </div>
        </div>
    );
};

export default BuyPasses;  
